import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ExtractionStage } from '../../../api/listeners/document-extraction'
import { getEducationLevels } from '../../../api/dict/api'
import { Button } from '../../ui/button'
import { ListenerEducation, RecognizedEducation, recognizedEducationPackets } from './recognized-education'
import css from './ui.recognition.module.scss'

type Props = { stage: ExtractionStage | null; education: ListenerEducation; onEducation: (value: RecognizedEducation, expected: ListenerEducation) => boolean; onPendingChange?: (pending: boolean) => void }

export const RecognizedEducationTransfer = ({ stage, education, onEducation, onPendingChange }: Props) => {
    const baseline = useRef({ ...education }), apply = useRef(onEducation)
    apply.current = onEducation
    const pending = useRef(false), pendingCallback = useRef(onPendingChange)
    pendingCallback.current = onPendingChange
    const reportPending = useCallback((value: boolean) => {
        pending.current = value
        pendingCallback.current?.(value)
    }, [])
    const { packets, needsReview } = useMemo(() => recognizedEducationPackets(stage), [stage])
    const [selected, setSelected] = useState<number | null>(null), [retry, setRetry] = useState(0)
    const [state, setState] = useState<{ phase: 'idle' | 'loading' | 'applied' | 'review'; message: string; value?: RecognizedEducation; retryable?: boolean }>({ phase: 'idle', message: '' })
    const completed = useRef('')
    const index = selected ?? (packets.length === 1 && !needsReview ? 0 : null)
    const packet = index === null ? undefined : packets[index]
    const packetKey = packet ? JSON.stringify(packet) : ''

    useEffect(() => {
        if (!packetKey) { reportPending(false); return }
        const packet = JSON.parse(packetKey) as NonNullable<typeof packets[number]>
        const key = JSON.stringify([stage?.stageID, packetKey, retry])
        if (completed.current === key) { reportPending(false); return }
        let active = true
        reportPending(!!packet.higherEducation)
        const unavailable = () => setState({ phase: 'review', message: 'Не удалось однозначно выбрать «Высшее образование» из справочника. Данные не перенесены. Повторите проверку.', retryable: true })
        const transfer = async () => {
            const patch: RecognizedEducation = { ...(packet.diplom ? { diplom: packet.diplom } : {}), ...(packet.maidenName ? { maidenName: packet.maidenName } : {}) }
            if (packet.higherEducation) {
                setState({ phase: 'loading', message: 'Выбираем «Высшее образование» из справочника…' })
                try {
                    const response = await getEducationLevels()
                    if (!active) return
                    const levels = response.status === 200 && Array.isArray(response.data) ? response.data.filter(level => level.educationLevelName.trim().toLocaleLowerCase('ru') === 'высшее образование'
                        && Number.isSafeInteger(level.educationLevelID) && level.educationLevelID > 0) : []
                    if (levels.length !== 1) { completed.current = key; unavailable(); return }
                    patch.educationLevelID = levels[0].educationLevelID
                    patch.educationLevel = levels[0].educationLevelName
                } catch {
                    if (active) { completed.current = key; unavailable() }
                    return
                }
            }
            if (!active) return
            completed.current = key
            if (!apply.current(patch, baseline.current)) {
                setState({ phase: 'review', message: 'Данные об образовании не перенесены: анкета или её поля уже изменены. Проверьте «Основные данные».' })
                return
            }
            setState({ phase: 'applied', message: 'Распознанные данные об образовании перенесены в «Основные данные». Сохраните анкету, чтобы записать изменения.', value: patch })
        }
        void transfer().finally(() => { if (active) reportPending(false) })
        return () => { active = false; reportPending(false) }
    }, [packetKey, stage?.stageID, retry, reportPending])

    useEffect(() => { onPendingChange?.(pending.current) }, [stage, onPendingChange])

    if (!packets.length && !needsReview) return null
    return <section className={css.group} aria-label="Перенос документа об образовании">
        <h5>Документ об образовании слушателя</h5>
        {index === null ? <>
            <p className={css.warning}>{packets.length ? 'Найдено несколько документов об образовании или есть неоднозначные реквизиты. Проверьте оригинал и выберите один документ.'
                : 'Реквизиты документа об образовании требуют проверки. Неполный документ не заменяет заполненное поле.'}</p>
            {packets.map((value, itemIndex) => <p key={JSON.stringify(value)}><Button onClick={() => setSelected(itemIndex)}>
                Перенести: {[value.diplom, value.higherEducation ? 'Высшее образование' : '', value.maidenName].filter(Boolean).join(' · ')}
            </Button></p>)}
        </> : <>
            <p role="status" aria-live="polite" className={state.phase === 'review' ? css.warning : undefined}>{state.message}</p>
            {state.value?.diplom && <p>{state.value.diplom}</p>}
            {state.value?.educationLevel && <p>Уровень образования: {state.value.educationLevel}</p>}
            {state.value?.maidenName && <p>Фамилия в дипломе: {state.value.maidenName}</p>}
            {state.retryable && <Button onClick={() => setRetry(value => value + 1)}>Повторить выбор уровня образования</Button>}
        </>}
    </section>
}
